import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Check, Users } from "lucide-react";
import { Eyebrow, Page, PageHeader, Reveal } from "@/components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, SERVICES } from "@/constants/site";

export default function Services() {
  return (
    <Page>
      <PageHeader
        eyebrow="Our Services"
        title={
          <>
            Every kind of labour, <span className="italic text-[#86EFAC]">done properly.</span>
          </>
        }
        copy="From berry fields to greenhouse rows to cannery lines — Barnala crews handle the work that keeps BC's food industry moving."
      />

      <section className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl space-y-20">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id}>
              <article
                data-testid={`service-detail-${s.id}`}
                className={`grid gap-10 lg:grid-cols-2 lg:items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="overflow-hidden rounded-2xl border border-white/10">
                  <img src={s.image} alt={s.title} className="aspect-[4/3] w-full object-cover" />
                </div>
                <div>
                  <span className="font-mono text-sm font-semibold text-[#22C55E]">{s.num}</span>
                  <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{s.title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-[#F6F4EE]/70">{s.blurb}</p>
                  <ul className="mt-6 space-y-3">
                    {s.tasks.map((task) => (
                      <li key={task} className="flex items-start gap-3 text-sm text-[#F6F4EE]/80 sm:text-base">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#22C55E]" />
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#0B1F17] px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>Crew Sizing & Scheduling</Eyebrow>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Reveal delay={0.1}>
              <div className="h-full rounded-2xl border border-white/10 bg-[#163B2C]/40 p-8">
                <Users className="h-6 w-6 text-[#22C55E]" />
                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight sm:text-2xl">
                  Flexible crew sizes
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/65 sm:text-base">
                  Need two pruners for a week or thirty pickers for the harvest rush? We scale crews up and down to
                  match your workload — daily, weekly or full-season arrangements.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="h-full rounded-2xl border border-white/10 bg-[#163B2C]/40 p-8">
                <CalendarClock className="h-6 w-6 text-[#22C55E]" />
                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight sm:text-2xl">
                  Short-notice dispatch
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/65 sm:text-base">
                  Weather windows and processing schedules change fast. Call us and we'll do everything possible to have
                  a crew on site next-day across the Lower Mainland, Fraser Valley and Greater Vancouver.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <a
                href={PHONE_TEL}
                data-testid="services-call-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#22C55E] px-7 py-4 text-sm font-semibold text-[#0F291E] transition-transform duration-300 hover:scale-105"
              >
                Call {PHONE_DISPLAY}
              </a>
              <Link
                to="/contact"
                data-testid="services-contact-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#86EFAC]"
              >
                Or send a request online
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
